import { api } from '../client';
import { session } from '../session';
import type { AuthSession, AuthUser } from '../types';

/**
 * Auth service — a thin, typed mapping over `/api/v1/auth`.
 * Services never hold state; they call the API and return domain objects.
 */

export interface LoginPayload {
  email: string;
  password: string;
  /** Tenant id or slug — sent as the tenant header. */
  tenant?: string;
  /** Required when the account has MFA enabled (server replies MFA_REQUIRED). */
  mfaToken?: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  tenantName: string;
  tenantSlug?: string;
}

/** The server signals "password ok, now give me a TOTP code" with this code. */
export const MFA_REQUIRED = 'MFA_REQUIRED';

export const authService = {
  async login(payload: LoginPayload): Promise<AuthSession> {
    // The tenant header must be set BEFORE the request so the API can resolve
    // which tenant this login belongs to.
    if (payload.tenant) session.setTenant(payload.tenant);

    const data = await api.post<AuthSession>(
      '/api/v1/auth/login',
      { email: payload.email, password: payload.password, mfaToken: payload.mfaToken },
      { authRequired: false },
    );
    session.save(data);
    return data;
  },

  async register(payload: RegisterPayload): Promise<AuthSession> {
    const data = await api.post<AuthSession>('/api/v1/auth/register', payload, {
      authRequired: false,
    });
    session.save(data);
    return data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout', { refreshToken: session.refreshToken });
    } catch {
      // A failed server-side logout must never trap the user in a signed-in
      // shell — clear locally regardless.
    }
    session.clear();
  },

  me(): Promise<AuthUser> {
    return api.get<AuthUser>('/api/v1/auth/me');
  },

  mfaSetup(): Promise<{ secret: string; otpauthUrl: string }> {
    return api.post('/api/v1/auth/mfa/setup');
  },

  mfaEnable(token: string): Promise<{ backupCodes: string[] }> {
    return api.post('/api/v1/auth/mfa/enable', { token });
  },

  /** Either the account password or a current TOTP/backup code confirms this. */
  mfaDisable(payload: { password?: string; token?: string }): Promise<{ disabled: boolean }> {
    return api.post('/api/v1/auth/mfa/disable', payload);
  },

  /** "Remind me later" on the MFA suggestion — silences it for a while. */
  mfaSnooze(): Promise<{ mfaPromptSnoozedUntil: string }> {
    return api.post('/api/v1/auth/mfa/snooze');
  },

  // ── Password reset ──
  /** Always resolves the same way, whether or not the email exists. */
  forgotPassword(tenant: string, email: string): Promise<{ success: boolean }> {
    if (tenant) session.setTenant(tenant);
    return api.post('/api/v1/auth/forgot-password', { email }, { authRequired: false });
  },

  resetPassword(token: string, password: string): Promise<{ success: boolean }> {
    return api.post('/api/v1/auth/reset-password', { token, password }, { authRequired: false });
  },

  // ── OTP sign-in (email or SMS — exactly one of phone/email) ──
  requestOtp(
    tenant: string,
    identifier: { phone: string } | { email: string },
  ): Promise<{ success: boolean; expiresIn: number; channel: 'sms' | 'email' }> {
    if (tenant) session.setTenant(tenant);
    return api.post('/api/v1/auth/otp/request', identifier, { authRequired: false });
  },

  async verifyOtp(tenant: string, identifier: { phone: string } | { email: string }, code: string): Promise<AuthSession> {
    if (tenant) session.setTenant(tenant);
    const data = await api.post<AuthSession>(
      '/api/v1/auth/otp/verify',
      { ...identifier, code },
      { authRequired: false },
    );
    session.save(data);
    return data;
  },

  // ── OAuth ──
  /** Which social providers are usable (credentialed server-side). */
  oauthProviders(): Promise<{ providers: string[] }> {
    return api.get('/api/v1/auth/oauth/providers', { authRequired: false });
  },

  /**
   * Full-page redirect to the provider. Not an XHR — the browser must leave the
   * SPA so the provider can take over. The workspace travels in the URL and is
   * echoed back inside the signed state.
   */
  startOauth(tenant: string, provider: 'google' | 'microsoft'): void {
    if (tenant) session.setTenant(tenant);
    // Relative path: dev goes through Vite's /api proxy, prod through nginx.
    window.location.href = `/api/v1/auth/oauth/${provider}/authorize?workspace=${encodeURIComponent(tenant)}`;
  },

  /** Persist a session handed back by the OAuth callback fragment. */
  adoptSession(data: AuthSession): void {
    session.save(data);
  },
};
